/*
 * File: colors.js
 * Project: seesaw-marketplace-app
 *
 * Created by Brendan Michaelsen on June 16, 2022 at 1:32 PM
 * Copyright © 2022 Seesaw Technologies, LLC. All rights reserved.
 *
 * Last Modified: January 28, 2023 at 12:29 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Color Values
 */

export const ColorValuesCreator = (merchant) => ({

	// Brand
	brandPrimaryBase: merchant.colors?.brandPrimaryBase,
	brandPrimaryLighter: merchant.colors?.brandPrimaryLighter,
	brandBackground: merchant.colors?.brandBackground,

	// Primary
	white: '#FFFFFF',
	dark: '#4a4a4a',

	// Neutral
	neutralButtonBase: '#747888',
	neutralButtonLighter: '#EEF0F6',
	neutralBackground: '#EFF1F3',
	neutralBackgroundDarker: '#39393B',
	neutralHighlight: '#E2E2E2',
	neutralHighlightDarker: '#505054',
	neutralForm: '#DBDFEC',
	neutralText: '#7F8496',
	neutralBorder: 'rgba(74,74,74,.05)',

	// Success
	statusSuccessBase: '#25953E',
	statusSuccessLighter: '#EAFFEE',
	statusSuccessDarker: '#04290D',

	// Warning
	statusWarningBase: '#E1D037',
	statusWarningLighter: '#FFFDE7',
	statusWarningDarker: '#302B05',

	// Danger
	statusDangerBase: '#E13737',
	statusDangerLighter: '#FFEBEB',
	statusDangerDarker: '#360404',

	// Info
	statusInfoBase: '#1A4ED4',
	statusInfoLighter: '#E7EDFF',
	statusInfoDarker: '#0B1D4D',

	// Shadows
	navigationShadow: '0px 1px 1px rgb(0 0 0 / 15%)',
	elementShadow: '0px -3px 20px rgba(0, 0, 0, 0.06)',
	elementDarkShadow: '0px 4px 16px rgba(0 0 0 / 22%)',

	// Brands
	whatsAppBrand: '#25d366',
	facebookBrand: '#0075fa',
	twitterBrand: '#1da1f2',
	linkedinBrand: '#0a66c2',
	redditBrand: '#ff4500',
	pinterestBrand: '#e60019'
});


/**
 * Themes
 */

export const DarkTheme = (merchant) => {

	// Create color values
	const ColorValues = ColorValuesCreator(merchant);

	// Return theme
	return {

		// Brand
		brandPrimaryBase: ColorValues.brandPrimaryBase,
		brandPrimaryLighter: ColorValues.brandPrimaryLighter,

		// Success
		statusSuccessBase: ColorValues.statusSuccessBase,
		statusSuccessLighter: ColorValues.statusSuccessDarker,

		// Warning
		statusWarningBase: ColorValues.statusWarningBase,
		statusWarningLighter: ColorValues.statusWarningDarker,

		// Danger
		statusDangerBase: ColorValues.statusDangerBase,
		statusDangerLighter: ColorValues.statusDangerDarker,

		// Info
		statusInfoBase: ColorValues.statusInfoBase,
		statusInfoLighter: ColorValues.statusInfoDarker,

		// Background
		primaryBackground: ColorValues.dark,
		secondaryBackground: ColorValues.neutralBackgroundDarker,
		cardBackground: ColorValues.dark,

		// Text
		primaryText: ColorValues.white,
		secondaryText: ColorValues.neutralText,

		// Button
		primaryButton: ColorValues.brandPrimaryBase,
		secondaryButton: ColorValues.neutralButtonBase,
		darkButton: ColorValues.dark,
		lightButton: ColorValues.neutralBackgroundDarker,

		// Decoration
		primaryDecoration: ColorValues.white,
		secondaryDecoration: ColorValues.neutralBorder,
		layoutBorder: ColorValues.neutralText,
		navigationBorder: ColorValues.neutralText,
		inactiveElement: ColorValues.neutralText,
		placeholderPrimary: ColorValues.neutralBackgroundDarker,
		placeholderHighlight: ColorValues.neutralHighlightDarker,

		// Gradient
		primaryGradientStart: 'rgba(0,0,0,0)',
		primaryGradientEnd: 'rgba(0,0,0,1)',

		// Input
		inputBackground: ColorValues.dark,
		inputSecondayBackground: ColorValues.neutralBackground,
		inputBorder: ColorValues.neutralText,
		inputFocusBorder: ColorValues.neutralButtonBase,
		inputPlaceholder: ColorValues.neutralText,
	};
};

export const LightTheme = (merchant) => {

	// Create color values
	const ColorValues = ColorValuesCreator(merchant);

	// Return theme
	return {

		// Brand
		brandPrimaryBase: ColorValues.brandPrimaryBase,
		brandPrimaryLighter: ColorValues.brandPrimaryLighter,

		// Success
		statusSuccessBase: ColorValues.statusSuccessBase,
		statusSuccessLighter: ColorValues.statusSuccessLighter,

		// Warning
		statusWarningBase: ColorValues.statusWarningBase,
		statusWarningLighter: ColorValues.statusWarningLighter,

		// Danger
		statusDangerBase: ColorValues.statusDangerBase,
		statusDangerLighter: ColorValues.statusDangerLighter,

		// Info
		statusInfoBase: ColorValues.statusInfoBase,
		statusInfoLighter: ColorValues.statusInfoLighter,

		// Background
		primaryBackground: ColorValues.brandBackground,
		secondaryBackground: ColorValues.neutralBackground,
		cardBackground: ColorValues.white,

		// Text
		primaryText: ColorValues.dark,
		secondaryText: ColorValues.neutralText,

		// Button
		primaryButton: ColorValues.brandPrimaryBase,
		secondaryButton: ColorValues.neutralButtonBase,
		darkButton: ColorValues.dark,
		lightButton: ColorValues.brandPrimaryLighter,

		// Decoration
		primaryDecoration: ColorValues.dark,
		secondaryDecoration: ColorValues.neutralBorder,
		layoutBorder: ColorValues.neutralForm,
		navigationBorder: 'transparent',
		inactiveElement: ColorValues.neutralText,
		placeholderPrimary: ColorValues.neutralBackground,
		placeholderHighlight: ColorValues.neutralHighlight,

		// Gradient
		primaryGradientStart: 'rgba(255,255,255,0)',
		primaryGradientEnd: 'rgba(255,255,255,1)',

		// Input
		inputBackground: ColorValues.white,
		inputSecondayBackground: ColorValues.neutralBackground,
		inputBorder: ColorValues.neutralForm,
		inputFocusBorder: ColorValues.neutralButtonBase,
		inputPlaceholder: ColorValues.neutralForm,
	};
};
